import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Container,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import api from '../services/api';
import { toast } from 'react-toastify';

interface Seguradora {
  id: number;
  nome: string;
  createdAt: string;
  updatedAt: string;
}

const GerenciarSeguradorasPage = () => {
  const [seguradoras, setSeguradoras] = useState<Seguradora[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [seguradoraAtual, setSeguradoraAtual] = useState<Seguradora | null>(null);
  const [formData, setFormData] = useState({ nome: '' });

  const carregarSeguradoras = async () => {
    setLoading(true);
    try {
      const response = await api.get('/seguradoras');
      setSeguradoras(response.data);
    } catch (error) {
      console.error('Erro ao carregar seguradoras:', error);
      toast.error('Erro ao carregar seguradoras. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarSeguradoras();
  }, []);

  const handleOpenDialog = (seguradora?: Seguradora) => {
    if (seguradora) {
      setSeguradoraAtual(seguradora);
      setFormData({ nome: seguradora.nome });
    } else {
      setSeguradoraAtual(null);
      setFormData({ nome: '' });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSeguradoraAtual(null);
    setFormData({ nome: '' });
  };

  const handleSubmit = async () => {
    if (!formData.nome.trim()) {
      toast.error('O nome da seguradora é obrigatório');
      return;
    }

    try {
      if (seguradoraAtual) {
        await api.put(`/seguradoras/${seguradoraAtual.id}`, formData);
        toast.success('Seguradora atualizada com sucesso!');
      } else {
        await api.post('/seguradoras', formData);
        toast.success('Seguradora criada com sucesso!');
      }
      handleCloseDialog();
      carregarSeguradoras();
    } catch (error) {
      console.error('Erro ao salvar seguradora:', error);
      toast.error('Erro ao salvar seguradora. Por favor, tente novamente.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta seguradora?')) {
      try {
        await api.delete(`/seguradoras/${id}`);
        toast.success('Seguradora excluída com sucesso!');
        carregarSeguradoras();
      } catch (error) {
        console.error('Erro ao excluir seguradora:', error);
        toast.error('Erro ao excluir seguradora. Por favor, tente novamente.');
      }
    }
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Box sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" component="h1">
              Seguradoras
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nova
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell>Atualizado em</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  seguradoras.map((seguradora) => (
                    <TableRow key={seguradora.id}>
                      <TableCell>{seguradora.id}</TableCell>
                      <TableCell>{seguradora.nome}</TableCell>
                      <TableCell>{formatarData(seguradora.createdAt)}</TableCell>
                      <TableCell>{formatarData(seguradora.updatedAt)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(seguradora)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(seguradora.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Dialog para criar/editar seguradora */}
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {seguradoraAtual ? 'Editar Seguradora' : 'Nova Seguradora'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nome da Seguradora"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.nome}
              onChange={(e) => setFormData({ nome: e.target.value.toUpperCase() })}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained">
              {seguradoraAtual ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default GerenciarSeguradorasPage; 